import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Typography,
  Box,
  Collapse,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  AddCircle as AddCircleIcon,
  Email as EmailIcon,
  LockOpen as LockOpenIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Gavel as GavelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GppGood as GppGoodIcon,
  GppBad as GppBadIcon,
} from '@mui/icons-material';
import { getAssessmentTimeline } from 'services/vendor-assessment-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';

const ACTION_ICON_MAP = {
  CREATE_ASSESSMENT: <AddCircleIcon fontSize="small" />,
  SEND_INVITATION: <EmailIcon fontSize="small" />,
  UNLOCK_CONTROLS: <LockOpenIcon fontSize="small" />,
  APPROVE_ASSESSMENT: <CheckCircleIcon fontSize="small" />,
  REJECT_ASSESSMENT: <CancelIcon fontSize="small" />,
  REQUEST_EXECUTIVE_APPROVAL: <GavelIcon fontSize="small" />,
  EVIDENCE_SCAN_PASSED: <GppGoodIcon fontSize="small" />,
  EVIDENCE_SCAN_FAILED: <GppBadIcon fontSize="small" />,
};

const CATEGORY_COLOR_MAP = {
  creation: 'primary',
  vendor: 'info',
  review: undefined,
  executive: 'warning',
  system: 'grey',
  security: 'error',
};

const getTimelineDotColor = (entry) => {
  if (entry.action === 'REJECT_ASSESSMENT') return 'error';
  if (entry.action === 'APPROVE_ASSESSMENT') return 'success';
  return CATEGORY_COLOR_MAP[entry.actionCategory] || 'grey';
};

const TimelineEntryDetails = ({ details, t }) => {
  if (!details) return null;

  const rows = [];

  if (details.previousStatus && details.newStatus) {
    rows.push({ label: t('gap-analysis:assessment.timeline.statusChange'), value: `${details.previousStatus} → ${details.newStatus}` });
  } else if (details.status) {
    rows.push({ label: t('common:labels.status'), value: details.status });
  }

  if (details.notes) {
    rows.push({ label: t('gap-analysis:assessment.timeline.notes'), value: details.notes });
  }

  if (details.justification) {
    rows.push({ label: t('gap-analysis:assessment.timeline.justification'), value: details.justification });
  }

  if (details.controlIds && Array.isArray(details.controlIds)) {
    rows.push({ label: t('gap-analysis:assessment.timeline.controlsUnlocked'), value: `${details.controlIds.length} control(s)` });
  }

  if (rows.length === 0) return null;

  return (
    <Box mt={1} pl={1} borderLeft="2px solid" borderColor="divider">
      {rows.map(({ label, value }) => (
        <Typography key={label} variant="caption" display="block" color="text.secondary">
          <strong>{label}:</strong> {value}
        </Typography>
      ))}
    </Box>
  );
};

const TimelineEntryItem = ({ entry, isLast, t }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!entry.details && Object.keys(entry.details).some(
    (k) => !['assessmentId', 'executiveUserId', '_id', 'companyKey'].includes(k)
  );

  return (
    <TimelineItem>
      <TimelineOppositeContent color="text.secondary" sx={{ maxWidth: 160, pt: '12px' }}>
        <Typography variant="caption">
          {formatDateTimeToDateTimeBR(entry.timestamp)}
        </Typography>
      </TimelineOppositeContent>

      <TimelineSeparator>
        <TimelineDot color={getTimelineDotColor(entry)}>
          {ACTION_ICON_MAP[entry.action] || <AddCircleIcon fontSize="small" />}
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>

      <TimelineContent sx={{ py: '12px', px: 2 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="body2" fontWeight="medium">
              {entry.actionLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.actor}
            </Typography>

            {hasDetails && (
              <Collapse in={expanded}>
                <TimelineEntryDetails details={entry.details} action={entry.action} t={t} />
              </Collapse>
            )}
          </Box>

          {hasDetails && (
            <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </TimelineContent>
    </TimelineItem>
  );
};

/**
 * AssessmentTimeline Component
 * Displays a chronological activity timeline for an assessment using audit logs.
 */
export const AssessmentTimeline = ({ assessmentId }) => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { data, isLoading, error } = useQuery(
    ['assessment-timeline', assessmentId],
    () => getAssessmentTimeline(assessmentId),
    {
      enabled: !!assessmentId,
      retry: false,
    }
  );

  const timeline = data?.data?.success ? data.data.data.timeline : [];

  if (isLoading) {
    return (
      <Box px={2}>
        {[1, 2, 3].map((i) => (
          <Box key={i} display="flex" gap={2} mb={2}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box flex={1}>
              <Skeleton width="40%" height={20} />
              <Skeleton width="25%" height={16} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" py={2}>
        {t('gap-analysis:assessment.timeline.unableToLoad')}
      </Typography>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" py={2}>
        {t('gap-analysis:assessment.timeline.noEvents')}
      </Typography>
    );
  }

  return (
    <Timeline sx={{ p: 0 }}>
      {timeline.map((entry, index) => (
        <TimelineEntryItem
          key={entry._id || index}
          entry={entry}
          isLast={index === timeline.length - 1}
          t={t}
        />
      ))}
    </Timeline>
  );
};
